import React, { FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { currencyFormatter } from 'shared/utils/currencyFormatter';
import { IPriceData, CablingType } from 'Quotes/types/store';
import { BearerType } from 'Quotes/QuoteBuilder/components/Configure/Bearer/BearerType';
import { connect, DispatchProp } from 'react-redux';
import { updatePriceQuoteAction, setCablingSuccessStateAction } from 'Quotes/actions';
import IPricedQuote from 'Quotes/types/pricedQuote';
import { IAppState } from 'reducers';
import { setInstallCost, setAnnualCost, setCablingType, hasCampusCrossConnect, extractCampusCrossConnectLocation, getCampusCrossConnectValue } from './utils/cabling';
import Alert, { Alerts } from 'shared/components/atoms/Alert';
import { CablingTypeSelection, QuoteStatus } from 'Quotes/types/quoteRecordAttributesBase';
import Spinner from 'shared/components/molecules/SpinnerWithText';
import RadioButton from 'shared/components/molecules/RadioButton';
import { IUpdatePriceQuotePayload } from 'Quotes/types/actions';
import { selectCablingTypeSelection, selectIsCablingChoiceMandatory, selectIsRapidActivationAvailable, selectRapidActivationLeadTime, selectStandardLeadTime } from 'Quotes/selectors';
import { ProductType } from 'Quotes/types/productTypes';

interface ICabling {
  className?: string;
  selectedPrice: IPriceData;
  bearer: BearerType | undefined;
  errorUpdating?: boolean;
  successUpdating?: boolean;
  loadingUpdate?: boolean;
  cablingUpdateSuccess?: boolean;
  quoteState?: QuoteStatus;
  quoteId?: string;
  cablingTypeSelection: CablingTypeSelection;
  isCablingChoiceMandatory: ReturnType<typeof selectIsCablingChoiceMandatory>;
  isRapidActivationAvailable: ReturnType<typeof selectIsRapidActivationAvailable>;
  rapidActivationLeadTime: ReturnType<typeof selectRapidActivationLeadTime>;
  standardLeadTime: ReturnType<typeof selectStandardLeadTime>;
  productType?: ProductType | null;
  updatePrice(
    quoteId: string,
    priceId: string,
    aEndCablingType: CablingType | null,
    bEndCablingType: CablingType | null,
    updatingCablingOrigin: IUpdatePriceQuotePayload['updatingCablingOrigin'],
    rapidActivationSelected?: boolean
  ): void;
  clearCablingSuccessState(): void;
}

export const Cabling: FunctionComponent<React.PropsWithChildren<ICabling>> = ({
  className,
  selectedPrice,
  bearer,
  errorUpdating,
  successUpdating,
  loadingUpdate,
  cablingUpdateSuccess,
  clearCablingSuccessState,
  updatePrice,
  quoteState,
  quoteId,
  cablingTypeSelection,
  isCablingChoiceMandatory,
  isRapidActivationAvailable,
  rapidActivationLeadTime,
  standardLeadTime,
  productType
}) => {
  const isCopper = bearer === BearerType.SMALL;
  const isSuccess = successUpdating && !loadingUpdate && cablingUpdateSuccess;
  const hideCablingType = !selectedPrice.a_end_cross_connect_cost && !selectedPrice.b_end_cross_connect_cost;

  useEffect(() => {
    clearCablingSuccessState();
  }, [selectedPrice.id, clearCablingSuccessState]);

  return (
    <div className={className}>
      <div
        className={cx('cabling_wrapper mt-3 pb-4', {
          readonly: quoteState === QuoteStatus.ORDERED,
        })}
      >
        {productType === ProductType.OpticalP2P && !isRapidActivationAvailable && standardLeadTime && <p className="font-weight-bold cabling-title">
           This optical P2P can be activated in {standardLeadTime} working days from order acceptance.
        </p>}

        {isRapidActivationAvailable && <div className="mb-5">
          <p className="font-weight-bold cabling-title">
            This Optical P2P can be activated
            {rapidActivationLeadTime ? (` within ${rapidActivationLeadTime} working days from order acceptance` + (standardLeadTime ? ` compared to our standard delivery of ${standardLeadTime} working days.` : '.')) : ' with an expedited timeframe'}
          </p>
          <div className="mt-3">
            <p className="cabling_option_label font-weight-bold">Request rapid activation?</p>
          </div>
          <div className="d-flex">
            <RadioButton
              className="cabling_option_radio mr-4"
              disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate}
              id="a_end_rapid_activation_yes"
              description="Yes"
              onClick={() => {
                updatePrice(
                  quoteId!,
                  selectedPrice.id,
                  null,
                  null,
                  'B',
                  true,
                );
              }}
              selected={selectedPrice.rapid_activation === true}
            />
            <RadioButton
              className="cabling_option_radio"
              disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate}
              id="a_end_rapid_activation_no"
              description="No"
              onClick={() => {
                updatePrice(
                  quoteId!,
                  selectedPrice.id,
                  null,
                  null,
                  'A',
                  false,
                );
              }}
              selected={selectedPrice.rapid_activation === false}
            />
          </div>
        </div>
        }

        {selectedPrice.rapid_activation !== true && <>
          <p className="font-weight-bold cabling-title">
            Cabling choice required for Neos Networks on-net buildings
          </p>
          <div className="mb-4">
            Cross connect cabling charges may apply to your order. Neos Networks will pass through the costs quoted by the
            Data Centre operator with an admin charge added per end as required
          </div>
          {!hideCablingType && (
            <div className="font-weight-bold">
              Cabling type: {isCopper ? 'Copper' : 'Fibre'} is subject to distance limitations
            </div>
          )}

          {(isCablingChoiceMandatory.A || hasCampusCrossConnect('A', selectedPrice.additional_services)) && (
            <>
              {hasCampusCrossConnect('A', selectedPrice.additional_services) ? (
                <>
                  <div className="mt-3">
                    <p className="cabling_option_label font-weight-bold">
                      Campus cross connect cabling for {selectedPrice.a_end_p_o_p} via {extractCampusCrossConnectLocation('A', selectedPrice.additional_services)}
                    </p>
                  </div>
                  <div className="h3 mt-3 text-muted">
                    {(() => {
                      const ccc_price = getCampusCrossConnectValue('A', 'Price', selectedPrice.additional_prices);
                      const ccc_cost = getCampusCrossConnectValue('A', 'Cost', selectedPrice.additional_prices);
                      const ccc_install = (ccc_price ? ccc_price.install : 0) + (ccc_cost ? ccc_cost.install : 0);
                      const ccc_annual = (ccc_price ? ccc_price.annual : 0) + (ccc_cost ? ccc_cost.annual : 0);
                      return (
                        <>
                          {ccc_install > 0 && (
                            <div className="cabling__segment-wrapper">
                              <p className="segment_label">Supplier installation charge</p>
                              <p>
                                <b>{currencyFormatter.format(ccc_install)}</b>
                              </p>
                            </div>
                          )}
                          {ccc_annual > 0 && (
                            <div className="cabling__segment-wrapper">
                              <p className="segment_label">Supplier annual cost</p>
                              <p>
                                <b>{currencyFormatter.format(ccc_annual)}</b>
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-3">
                    <p className="cabling_option_label font-weight-bold">Request cabling for {selectedPrice.a_end_p_o_p}?</p>
                  </div>
                  <div className="h3 mt-3 text-muted">
                    {setInstallCost(isCopper, selectedPrice.a_end_cross_connect_cost) > 0 && (
                      <div className="cabling__segment-wrapper">
                        <p className="segment_label">Supplier installation charge</p>
                        <p>
                          <b>{currencyFormatter.format(setInstallCost(isCopper, selectedPrice.a_end_cross_connect_cost))}</b>
                        </p>
                      </div>
                    )}
                    {setAnnualCost(isCopper, selectedPrice.a_end_cross_connect_cost) > 0 && (
                      <div className="cabling__segment-wrapper">
                        <p className="segment_label">Supplier annual cost</p>
                        <p>
                          <b>{currencyFormatter.format(setAnnualCost(isCopper, selectedPrice.a_end_cross_connect_cost))}</b>
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="d-flex">
                    <RadioButton
                      className="cabling_option_radio mr-4"
                      disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate || selectedPrice.rapid_activation}
                      id="a_end_cabling_yes"
                      description="Yes"
                      onClick={() => {
                        updatePrice(
                          quoteId!,
                          selectedPrice.id,
                          setCablingType(isCopper, true),
                          setCablingType(isCopper, !!selectedPrice.b_end_cabling_type),
                          'A',
                          undefined
                        );
                      }}
                      selected={selectedPrice.a_end_cabling_type !== null}
                    />
                    <RadioButton
                      className="cabling_option_radio"
                      disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate || selectedPrice.rapid_activation}
                      id="a_end_cabling_no"
                      description="No"
                      onClick={() => {
                        updatePrice(
                          quoteId!,
                          selectedPrice.id,
                          setCablingType(isCopper, false),
                          setCablingType(isCopper, !!selectedPrice.b_end_cabling_type),
                          'A',
                          undefined
                        );
                      }}
                      selected={selectedPrice.a_end_cabling_type === null && cablingTypeSelection.a_end_cabling_type_selected}
                    />
                  </div>
                  {(selectedPrice.a_end_cabling_type === null && cablingTypeSelection.a_end_cabling_type_selected) &&
                    <div className='mb-4'>
                      Neos Networks will provide the rack information where the customer is providing the cross connect.
                    </div>
                  }
                </>
              )}
            </>
          )}

          {(isCablingChoiceMandatory.B || hasCampusCrossConnect('B', selectedPrice.additional_services)) && (
            <>
              {hasCampusCrossConnect('B', selectedPrice.additional_services) ? (
                <>
                  <div className="mt-3">
                    <p className="cabling_option_label font-weight-bold">
                      Campus cross connect cabling for {selectedPrice.b_end_p_o_p} via {extractCampusCrossConnectLocation('B', selectedPrice.additional_services)}
                    </p>
                  </div>
                  <div className="h3 mt-3 text-muted">
                    {(() => {
                      const ccc_price = getCampusCrossConnectValue('B', 'Price', selectedPrice.additional_prices);
                      const ccc_cost = getCampusCrossConnectValue('B', 'Cost', selectedPrice.additional_prices);
                      const ccc_install = (ccc_price ? ccc_price.install : 0) + (ccc_cost ? ccc_cost.install : 0);
                      const ccc_annual = (ccc_price ? ccc_price.annual : 0) + (ccc_cost ? ccc_cost.annual : 0);
                      return (
                        <>
                          {ccc_install > 0 && (
                            <div className="cabling__segment-wrapper">
                              <p className="segment_label">Supplier installation charge</p>
                              <p>
                                <b>{currencyFormatter.format(ccc_install)}</b>
                              </p>
                            </div>
                          )}
                          {ccc_annual > 0 && (
                            <div className="cabling__segment-wrapper">
                              <p className="segment_label">Supplier annual cost</p>
                              <p>
                                <b>{currencyFormatter.format(ccc_annual)}</b>
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-3">
                    <p className="cabling_option_label font-weight-bold">Request cabling for {selectedPrice.b_end_p_o_p}?</p>
                  </div>
                  <div className="h3 mt-3 text-muted">
                    {setInstallCost(isCopper, selectedPrice.b_end_cross_connect_cost) > 0 && (
                      <div className="cabling__segment-wrapper">
                        <p className="segment_label">Supplier installation charge</p>
                        <p>
                          <b>{currencyFormatter.format(setInstallCost(isCopper, selectedPrice.b_end_cross_connect_cost))}</b>
                        </p>
                      </div>
                    )}
                    {setAnnualCost(isCopper, selectedPrice.b_end_cross_connect_cost) > 0 && (
                      <div className="cabling__segment-wrapper">
                        <p className="segment_label">Supplier annual cost</p>
                        <p>
                          <b>{currencyFormatter.format(setAnnualCost(isCopper, selectedPrice.b_end_cross_connect_cost))}</b>
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="d-flex">
                    <RadioButton
                      className="cabling_option_radio mr-4"
                      disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate || selectedPrice.rapid_activation}
                      id="b_end_cabling_yes"
                      description="Yes"
                      onClick={() => {
                        updatePrice(
                          quoteId!,
                          selectedPrice.id,
                          setCablingType(isCopper, !!selectedPrice.a_end_cabling_type),
                          setCablingType(isCopper, true),
                          'B',
                          undefined
                        );
                      }}
                      selected={selectedPrice.b_end_cabling_type !== null}
                    />
                    <RadioButton
                      className="cabling_option_radio"
                      disabled={quoteState === QuoteStatus.ORDERED || loadingUpdate || selectedPrice.rapid_activation}
                      id="b_end_cabling_no"
                      description="No"
                      onClick={() => {
                        updatePrice(
                          quoteId!,
                          selectedPrice.id,
                          setCablingType(isCopper, !!selectedPrice.a_end_cabling_type),
                          setCablingType(isCopper, false),
                          'B',
                          undefined
                        );
                      }}
                      selected={selectedPrice.b_end_cabling_type === null && cablingTypeSelection.b_end_cabling_type_selected}
                    />
                  </div>
                  {(selectedPrice.b_end_cabling_type === null && cablingTypeSelection.b_end_cabling_type_selected) &&
                    <div className='mb-4'>
                      Neos Networks will provide the rack information where the customer is providing the cross connect.
                    </div>
                  }
                </>
              )}
            </>
          )}
        </>}

        {loadingUpdate && (
          <div className="spinner-wrapper">
            <Spinner text="Updating..." size="xlarge" />
          </div>
        )}

        {quoteState !== QuoteStatus.ORDERED && (
          <div className="pt-3 update-user-btn text-center">
            {isSuccess ? (
              <Alert alertType={Alerts.SUCCESS}>Configuration updated successfully</Alert>
            ) : errorUpdating ? (
              <Alert alertType={Alerts.WARNING}>Error: Configuration may not have been updated</Alert>
            ) : (
              ''
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styledCabling = styled(Cabling)`
  .readonly {
    opacity: 0.7;
  }

  .cabling-title {
    color: ${(props) => props.theme.colours.primaryB1};
  }

  .cabling_option_label {
    color: ${(props) => props.theme.colours.secondary};
  }

  .cabling_option_radio {
    width: 210px;
  }

  .cabling_wrapper {
  }

  .spinner-wrapper {
    position: absolute;
    right: 0;
    top: 0;
    left: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
  }

  .cabling__segment-wrapper {
    display: flex;
    .segment_label {
      width: 100%;
    }

    p {
      font-size: 20px;
    }
  }
`;

const mapDispatchToProps = (dispatch: DispatchProp['dispatch']) => ({
  updatePrice: (
    quoteId: string,
    priceId: string,
    aEndCablingType: CablingType | null,
    bEndCablingType: CablingType | null,
    updatingCablingOrigin: IUpdatePriceQuotePayload['updatingCablingOrigin'],
    rapidActivation?: boolean,
  ) => {
    const updatedValues = {
      a_end_cabling_type: aEndCablingType,
      b_end_cabling_type: bEndCablingType,
      rapid_activation: rapidActivation,
    } as IPricedQuote;

    return dispatch(
      updatePriceQuoteAction(
        priceId,
        updatedValues,
        true,
        quoteId,
        false,
        updatingCablingOrigin,
        rapidActivation !== undefined
      )
    )
  },
  clearCablingSuccessState: () => dispatch(setCablingSuccessStateAction(false)),
});

const mapStateToProps = (state: IAppState) => ({
  successUpdating: state.quoteBuilder.pricing.pricingProgress.updatingSuccess,
  errorUpdating: state.quoteBuilder.pricing.pricingProgress.error,
  loadingUpdate: state.quoteBuilder.pricing.pricingProgress.loadingUpdate,
  quoteId: state.quoteBuilder.currentQuoteId,
  cablingUpdateSuccess: state.quoteBuilder.pricing.pricingProgress.cablingSuccess,
  quoteMeta: state.quoteBuilder.quoteEndpointMeta,
  cablingTypeSelection: selectCablingTypeSelection(state),
  selectedPrice: state.quoteBuilder.pricing.selectedPrice,
  quoteState: state.quoteBuilder.state,
  bearer: state.quoteBuilder.quote.bearer,
  isCablingChoiceMandatory: selectIsCablingChoiceMandatory(state),
  isRapidActivationAvailable: selectIsRapidActivationAvailable(state),
  rapidActivationLeadTime: selectRapidActivationLeadTime(state),
  standardLeadTime: selectStandardLeadTime(state),
  productType: state.quoteBuilder.quote.productType,
});

export default connect(mapStateToProps, mapDispatchToProps)(styledCabling);
